import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getBirds, Bird } from '../../api/birds';
import { getSpecies, Species } from '../../api/species';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface CreatePhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => Promise<void>;
}

const CreatePhotoModal: React.FC<CreatePhotoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    caption: '',
    birdId: '',
    speciesId: '',
    tags: [] as string[],
    isAvatar: false,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // State for birds and species
  const [birds, setBirds] = useState<Bird[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  // Fetch birds and species when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBirdsAndSpecies();
    }
  }, [isOpen]);

  const fetchBirdsAndSpecies = async () => {
    setLoadingData(true);
    try {
      const [birdsData, speciesData] = await Promise.all([
        getBirds(),
        getSpecies()
      ]);
      setBirds(birdsData);
      setSpecies(speciesData);
    } catch (error) {
      console.error('Error fetching birds and species:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    setLoading(true);

    try {
      const submitFormData = new FormData();
      submitFormData.append('file', selectedFile);
      submitFormData.append('caption', formData.caption);
      submitFormData.append('isAvatar', formData.isAvatar.toString());
      
      if (formData.birdId) {
        submitFormData.append('birdId', formData.birdId);
      }
      
      if (formData.speciesId) {
        submitFormData.append('speciesId', formData.speciesId);
      }
      
      if (formData.tags.length > 0) {
        formData.tags.forEach(tag => {
          submitFormData.append('tags', tag);
        });
      }

      await onSubmit(submitFormData);
      
      // Reset form
      setFormData({
        caption: '',
        birdId: '',
        speciesId: '',
        tags: [],
        isAvatar: false,
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setTagInput('');
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleClose = () => {
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setSelectedFile(null);
    setFormData({
      caption: '',
      birdId: '',
      speciesId: '',
      tags: [],
      isAvatar: false,
    });
    setTagInput('');
    onClose();
  };

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Upload Photo" onClose={handleClose} />
      <form id="create-photo-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label htmlFor="file" className="block text-sm font-medium text-slate-700">
                    Photo File *
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      {previewUrl ? (
                        <div className="mb-4">
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="mx-auto h-32 w-32 object-cover rounded-lg"
                          />
                        </div>
                      ) : (
                        <svg
                          className="mx-auto h-12 w-12 text-slate-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      <div className="flex text-sm text-slate-600">
                        <label
                          htmlFor="file"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-cyan-600 hover:text-cyan-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-cyan-500"
                        >
                          <span>{selectedFile ? 'Change file' : 'Upload a file'}</span>
                          <input
                            id="file"
                            name="file"
                            type="file"
                            accept="image/*"
                            required
                            onChange={handleFileChange}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-slate-500">PNG, JPG, WEBP up to 5MB</p>
                      {selectedFile && (
                        <p className="text-sm text-slate-600 mt-2">
                          Selected: {selectedFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div>
                  <label htmlFor="caption" className="block text-sm font-medium text-slate-700">
                    Caption
                  </label>
                  <input
                    type="text"
                    id="caption"
                    value={formData.caption}
                    onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Enter photo caption"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bird Selection */}
                  <div>
                    <label htmlFor="birdId" className="block text-sm font-medium text-slate-700">
                      Bird (Optional)
                    </label>
                    <select
                      id="birdId"
                      value={formData.birdId}
                      onChange={(e) => setFormData(prev => ({ ...prev, birdId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      disabled={loadingData}
                    >
                      <option key="empty-option-1753461297337" value="">Select a bird...</option>
                      {birds.map((bird) => (
                        <option key={bird.id} value={bird.id}>
                          {bird.bandId} - {bird.species?.commonName || 'Unknown species'}
                        </option>
                      ))}
                    </select>
                    {loadingData && (
                      <p className="mt-1 text-xs text-slate-500">Loading birds...</p>
                    )}
                  </div>

                  {/* Species Selection */}
                  <div>
                    <label htmlFor="speciesId" className="block text-sm font-medium text-slate-700">
                      Species (Optional)
                    </label>
                    <select
                      id="speciesId"
                      value={formData.speciesId}
                      onChange={(e) => setFormData(prev => ({ ...prev, speciesId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      disabled={loadingData}
                    >
                      <option key="empty-option-1753461297337" value="">Select a species...</option>
                      {species.map((spec) => (
                        <option key={spec.id} value={spec.id}>
                          {spec.commonName} ({spec.genus} {spec.species})
                        </option>
                      ))}
                    </select>
                    {loadingData && (
                      <p className="mt-1 text-xs text-slate-500">Loading species...</p>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-slate-700">
                    Tags
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      className="flex-1 min-w-0 block w-full border-gray-300 rounded-none rounded-l-md focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Add tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-slate-50/80 text-slate-500 text-sm"
                    >
                      Add
                    </button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:bg-indigo-200 hover:text-cyan-500 focus:outline-none focus:bg-cyan-500 focus:text-white"
                          >
                            <XMarkIcon className="h-2 w-2" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Avatar checkbox */}
                <div className="flex items-center">
                  <input
                    id="isAvatar"
                    type="checkbox"
                    checked={formData.isAvatar}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAvatar: e.target.checked }))}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isAvatar" className="ml-2 block text-sm text-slate-900">
                    Set as avatar
                  </label>
                </div>
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={handleClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="create-photo-form"
            disabled={loading || !selectedFile}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Uploading...' : 'Upload Photo'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default CreatePhotoModal; 